import React, { useContext, useEffect, useState } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { Logo } from "../../icon/logo";
import { cn, mapEdgesToNodes } from "../../../lib/helpers";
import { SiteContext } from "../../global/site-context";
import { HamburgerIcon } from "../../icon/hamburger";
import { CloseIcon } from "../../icon/close-x";
import { pageDocPath, trackPath } from "../../../../helpers";
import { appendUtms, clientAttribution } from "../../../lib/client-attribution";
import { TinyDownArrow } from "../../icon/tiny-down-arrow";

// @gotcha importing these constants can cause build failtures

// @todo turn into site setting tokens
export const SIGNUP_URL = "/signup/";
export const SIGNUP_PHRASE = "Start free trial";

export const LOGIN_URL = "https://app.commonroom.io";
export const LOGIN_PHRASE = "Log in";

export const DEMO_URL = "/demo/";
export const DEMO_PHRASE = "Request demo";

export const ATTRIBUTION_PARAM = "website_attribution";

const DEFAULT_COLOR = "dark-80";

export const MAIN_MENU_FOOTER = [
  {
    label: "Solutions",
    to: null,
    links: [
      {
        label: "RevOps",
        to: "/solutions/revops/",
      },
      {
        label: "Sales (SDR)",
        to: "/solutions/sales-development/",
      },
      {
        label: "Sales (AE)",
        to: "/solutions/sales/",
      },
      {
        label: "Demand Generation",
        to: "/solutions/demand-generation/",
      },
      {
        label: "Account-based",
        to: "/solutions/abm/",
      }
    ],
  },
  {
    label: "Resources",
    links: [
      {
        label: "Resource Hub",
        to: "/resources/",
      },
      {
        label: "Customers",
        to: "/customers/",
        footerOnly: true,
      },
      {
        label: "Blog",
        to: "/blog/",
      },
      {
        label: "Academy",
        to: "/academy/",
      },
      {
        label: "Events & Webinars",
        to: "/events/",
      },
      {
        label: "Playbooks",
        to: "/playbooks/",
      },
      {
        label: "Signal Guides",
        to: "/resources/signals/",
      },
      {
        label: "Docs",
        to: "/docs/",
      },
      {
        label: "Community",
        to: "/community/",
      },
      {
        label: "Podcast",
        to: "/podcast/",
      },
    ],
  },
  {
    label: "Customers",
    to: "/customers/",
  },
  {
    label: "Pricing",
    to: "/pricing/",
  },
];

// Same Sanity menu document used by the footer and global nav.
const query = graphql`
  query MobileNavMenuQuery {
    menus: allSanityMenu(
      filter: { _id: { eq: "0ae244a8-0e78-482c-bb63-0422b316fcce" } }
    ) {
      edges {
        node {
          items {
            _key
            title
            reference {
              ... on SanityPage {
                title
                slug {
                  current
                }
              }
              ... on SanityOffering {
                title
                slug {
                  current
                }
              }
            }
            href
            children {
              _key
              title
              reference {
                ... on SanityPage {
                  title
                  slug {
                    current
                  }
                }
                ... on SanityOffering {
                  title
                  slug {
                    current
                  }
                }
              }
              href
              children {
                _key
                title
                reference {
                  ... on SanityPage {
                    title
                    slug {
                      current
                    }
                  }
                  ... on SanityOffering {
                    title
                    slug {
                      current
                    }
                  }
                }
                href
              }
            }
          }
        }
      }
    }
  }
`;

const makeUrl = (item) =>
  item.href ? item.href : item.reference ? pageDocPath(item.reference) : null;

const makeProductMenu = (items) => {
  const product = items?.find(
    (item) => (item.title || item.reference?.title) === "Product"
  );

  // Product children are group headers (e.g. "Common Room", "Zoom"); their
  // own children are the actual product links, indented beneath the group.
  return {
    label: "Product",
    to: null,
    links: (product?.children || []).flatMap((group) => {
      const groupLink = {
        label: group.title || group.reference?.title,
        to: makeUrl(group),
      };
      const groupLinks = (group.children || []).map((link) => ({
        label: link.title || link.reference?.title,
        to: makeUrl(link),
        indent: true,
      }));

      return [groupLink, ...groupLinks];
    }),
  };
};

const makeMainMenu = (productMenu) =>
  [productMenu, ...MAIN_MENU_FOOTER]
    .map((l) =>
      l.links ? { ...l, links: l.links.filter((m) => !m.footerOnly) } : l
    )
    .concat([
      {
        label: LOGIN_PHRASE,
        href: LOGIN_URL,
      },
    ]);

/**
 * collapsed state can be managed externally or left internal
 */
export const CollapsibleMenu = ({
  color,
  className,
  isMainNav,
  isCollapsed,
  setCollapsed,
}) => {
  const site = useContext(SiteContext);
  const data = useStaticQuery(query);
  const menus = mapEdgesToNodes(data.menus);
  const productMenu = makeProductMenu(menus[0]?.items);
  const mainMenu = makeMainMenu(productMenu);
  const [isCollapsedSelf, setCollapsedSelf] = useState(true);
  const [expandedMenu, setExpandedMenu] = useState("");
  const path = trackPath(site?.doc);

  const isToggled = setCollapsed ? isCollapsed : isCollapsedSelf;

  const menuLinks = mainMenu.concat([
    {
      label: DEMO_PHRASE,
      href: `${DEMO_URL}?${ATTRIBUTION_PARAM}=collapse-nav`,
      className:
        "text-2xl bg-green-emerald shadow-xl hover:bg-black transform scale-100 hover:scale-95 transition-nav w-full text-white p-2 py-5 block text-center mt-28 md:mb-0",
      attributes: {
        onClick: () => {
          site.metrics.logEvent({
            label: DEMO_PHRASE.toLowerCase(),
            category: "button_click",
            action: "nav-demo",
          });
        },
      },
    },
  ]);

  useEffect(() => {
    if (!isToggled) {
      clientAttribution({
        isActive: site.isActive,
        storage: site.metrics.analytics.storage,
        referrer: document.referrer,
        callback: (urlParams) => {
          appendUtms({
            dom: document,
            urlParams,
            doc: site.doc,
            storage: site.metrics.analytics.storage,
          });
        },
      });
    }
  }, [isToggled]);

  return (
    <>
      {!isToggled && (
        <a
          className="fixed top-0 left-0 h-screen w-screen bg-white md:opacity-20 z-100"
          onClick={(e) => {
            setCollapsed ? setCollapsed(true) : setCollapsedSelf(true);
            e.stopPropagation();
          }}
        ></a>
      )}
      <div
        className={cn(
          !isCollapsed && isMainNav ? "absolute top-3 right-4" : "relative",
          "text-white transition-link cursor-pointer z-100",
          className
        )}
        onClick={(e) => {
          setCollapsed
            ? setCollapsed(!isToggled)
            : setCollapsedSelf(!isToggled);
          e.stopPropagation();
        }}
      >
        <div
          className={cn(
            "rounded-2xl w-12 h-9",
            "flex items-center justify-center text-center text-2xl",
            color ? `bg-${color}` : null,
            color === DEFAULT_COLOR ? "hover:bg-dark-80 transition-link" : "",
            site.banners.length > 0 && !isToggled
              ? "fixed right-5 top-3 md:top-11"
              : "mt-0.5"
          )}
        >
          {isToggled ? (
            <div
              className={cn(
                "flex flex-wrap justify-center content-center h-full",
                (!color && site.isDarkBg) || !isMainNav
                  ? "text-white"
                  : "text-black"
              )}
            >
              <HamburgerIcon size={color ? 14 : null} />
            </div>
          ) : (
            <div className={isMainNav ? "text-black" : "text-white"}>
              <CloseIcon />
            </div>
          )}
        </div>

        {!isToggled && (
          <>
            <span
              className="md:hidden text-primary fixed left-4 z-100"
              style={{ top: 19 }}
            >
              <LogoLink>
                <Logo className="h-8" />
              </LogoLink>
            </span>
            <ul className="block z-100 fixed top-10 md:top-20 right-0 md:right-10 w-screen md:w-auto h-screen md:h-auto px-3 pt-16 pb-10 md:py-4 md:px-2 md:shadow-lg md:bg-white md:rounded-xl flex flex-col text-left text-black leading-none text-xl md:text-base font-semibold whitespace-nowrap">
              {menuLinks &&
                menuLinks.map((item, i) => {
                  const linkClass =
                    "block p-3 py-2 md:py-1 hover:bg-dark-5 rounded-md";

                  return item.links ? (
                    <ExpandableMenuItem
                      link={item}
                      exandedMenu={expandedMenu}
                      setExandedMenu={setExpandedMenu}
                      key={`sticky-nav-${item.label}-${i}`}
                      linkClass={linkClass}
                    />
                  ) : (
                    <li
                      className={cn(
                        i === menuLinks.length - 1
                          ? "flex-grow flex items-start"
                          : null,
                        "mb-4 md:mb-0"
                      )}
                      key={i}
                    >
                      <MenuLink link={item} linkClass={linkClass} />
                    </li>
                  );
                })}
            </ul>
          </>
        )}
      </div>
    </>
  );
};

const ExpandableMenuItem = ({
  link,
  exandedMenu,
  setExandedMenu,
  linkClass,
}) => (
  <li className="mb-4 md:mb-0">
    <span
      className={cn(
        "flex justify-between items-center p-3 md:py-1.5 hover:bg-dark-5 rounded-md relative",
        link.class
      )}
      onClick={(e) => {
        setExandedMenu(exandedMenu !== link.label ? link.label : "");
        e.stopPropagation();
      }}
    >
      {link.label}
      <span className="ml-8 mr-4">
        <TinyDownArrow rotate={exandedMenu !== link.label ? 270 : 0} />
      </span>
    </span>
    {exandedMenu === link.label && (
      <ul className="font-normal">
        {link.links.map((link, i) => (
          <li
            key={i}
            className={
              link.to || link.href ? (link.indent ? "ml-6" : "ml-4") : "ml-2"
            }
          >
            {link.to || link.href ? (
              <MenuLink link={link} linkClass={linkClass} showIndent={true} />
            ) : link.divider ? (
              <hr className="bg-dark-20 my-4" style={{ height: "2px" }} />
            ) : (
              <span className="block p-2 my-2 text-secondary whitespace-nowrap">
                {link.label}
              </span>
            )}
          </li>
        ))}
      </ul>
    )}
  </li>
);

/**
 * Force a full page reload on the homepage for A/B tests.
 */
export const LogoLink = (props) => (
  <a href="/" {...props} aria-label="Logo link to homepage"></a>
);

export const MenuLink = React.forwardRef(
  ({ link, linkClass, showIndent }, ref) => {
    return link.href ? (
      <a
        href={link.href}
        className={cn(link.className, linkClass, showIndent && link.indent ? "pl-1.5 ml-0 border-l border-light-20" : null)}
        {...link.attributes}
      >
        {link.label}
      </a>
    ) : (
      <a
        href={link.to ? link.to : "#"}
        className={cn(link.className, linkClass, showIndent && link.indent ? "pl-1.5 ml-0 border-l border-light-20" : null)}
        ref={ref}
        {...link.attributes}
      >
        {link.label}
        {link.links && <TinyDownArrow className="ml-1 mt-1 opacity-60" />}
      </a>
    );
  }
);
